import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { GstRestS, UploadedFile, GstCredential } from "../../rest/gst-rest-service";

interface GstReconState {
  credentials: GstCredential[];
  purchaseFiles: UploadedFile[];
  purchaseFilesRecordsTill: string | null;
  selectedGstin: string | null;
  credentialsLoaded: boolean;
  filesLoaded: boolean;
}

const initialState: GstReconState = {
  credentials: [],
  purchaseFiles: [],
  purchaseFilesRecordsTill: null,
  selectedGstin: null,
  credentialsLoaded: false,
  filesLoaded: false,
};

export const fetchGstCredentials = createAsyncThunk(
  "gstRecon/fetchCredentials",
  async (_, { rejectWithValue }) => {
    try {
      const res = await GstRestS.getCredentials();
      return (res.success && Array.isArray(res.data) ? res.data : []) as GstCredential[];
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const fetchGstPurchaseFiles = createAsyncThunk(
  "gstRecon/fetchPurchaseFiles",
  async (_, { rejectWithValue }) => {
    try {
      return await GstRestS.getPurchaseFiles();
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const deleteGstPurchaseFile = createAsyncThunk(
  "gstRecon/deleteFile",
  async ({ fileId, isDashboardDelete }: { fileId: string; isDashboardDelete?: boolean }, { rejectWithValue }) => {
    try {
      const res = await GstRestS.deletePurchaseFile(fileId, isDashboardDelete);
      if (res?.success === false) return rejectWithValue(res.message ?? "Delete failed");
      return fileId;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

const gstReconSlice = createSlice({
  name: "gstRecon",
  initialState,
  reducers: {
    setGstSelectedGstin: (state, action: PayloadAction<string | null>) => {
      state.selectedGstin = action.payload;
    },
    appendPurchaseFiles: (state, action: PayloadAction<UploadedFile[]>) => {
      const existing = new Set(state.purchaseFiles.map((f) => f.id));
      action.payload.forEach((f) => {
        if (!existing.has(f.id)) state.purchaseFiles.push(f);
      });
    },
    resetGstRecon: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGstCredentials.fulfilled, (state, action) => {
        state.credentials = action.payload;
        state.credentialsLoaded = true;
      })
      .addCase(fetchGstCredentials.rejected, (state) => {
        state.credentialsLoaded = true;
      })
      .addCase(fetchGstPurchaseFiles.fulfilled, (state, action) => {
        state.purchaseFiles = action.payload.files;
        state.purchaseFilesRecordsTill = action.payload.recordsTill;
        state.filesLoaded = true;
      })
      .addCase(deleteGstPurchaseFile.fulfilled, (state, action) => {
        state.purchaseFiles = state.purchaseFiles.filter((f) => f.id !== action.payload);
      });
  },
});

export const { setGstSelectedGstin, appendPurchaseFiles, resetGstRecon } = gstReconSlice.actions;
export const gstReconReducer = gstReconSlice.reducer;
